/**
 * Partner / PartnerGroup / PartnerGroupMember / Show are mutually recursive: a Show belongs to a
 * PartnerGroup, a PartnerGroup has Members, a Member has a Partner, and a Partner has Shows. Each
 * one's `mapProperties` calls the next one's at runtime, so none of those edges can be reduced to
 * an `import type`.
 *
 * Split across four modules that was a genuine import cycle
 * (partner-group-member -> partner -> show -> partner-group -> partner-group-member), which
 * webpack flagged and which can leave a class binding undefined at module-evaluation time.
 * Co-locating mutually recursive types in one module is the standard resolution - a cycle inside
 * a module is not a cycle between modules.
 *
 * The original four files remain as re-export shims, so every existing
 * `from '.../models/show'` import and every `Show.mapProperties(...)` call site is untouched.
 */
import { Episode } from "./episode";
import { Season } from "./season";

export class Partner {

    id: number | null = null;
    name: string = '';
    earningsYTD: number = 0;
    proStatus: string = '';
    shows: Show[] = [];
    membershipIds: PartnerGroupMember[] = [];

    static mapProperties(item: any): Partner {
        return{
            id: item.id,
            name: item.name?.replace(/ {2,}/g, ' '),
            earningsYTD: item.earnings_ytd,
            proStatus: item.pro_status,
            shows: item.shows ? item.shows.map((show: any) => Show.mapProperties(show)) : [],
            membershipIds: item.memberships
        }
    }
}

export class PartnerGroup {
    id: number | null = null;
    name: string = '';
    members: PartnerGroupMember[] = [];
    shows: Show[] = [];
    episodes: Episode[] = [];
    selectedEpisodesCount: number = 0;

    static mapProperties(item: any): PartnerGroup {
        if (item){
            return {
                id: item.id,
                name: item.name?.replace(/ {2,}/g, ' '),
                members: item.members.map((member: any) => PartnerGroupMember.mapProperties(member)),
                shows: (item.shows || []).map((show: any) => Show.mapProperties(show)),
                episodes: (item.episodes || []).map((episode: any) => Episode.mapProperties(episode)),
                selectedEpisodesCount: 0
            }
        }
        else{
            return null;
        }
    }
}

export class PartnerGroupMember {
    id: number = 0;
    allocationPercentage: number = 0;
    postDilution: boolean = false;
    partnerId: number = 0;
    partnerGroupId: number = 0;
    partner: Partner = new Partner();
    // episodes: Episode[] = [];
    postDilutionParentId: number | null = null;
    postDilutionChildren: PartnerGroupMember[] = [];

    //for front end only
    // isToggled = false;
    //selectedEpisodesCount: number = 0;
    expandDetails = true; //default showing post dilutors dropdowns on partner-group=list view

    static mapProperties(item: any): PartnerGroupMember {
        return {
            id: item.id,
            allocationPercentage: parseFloat(item.allocation_percentage),
            postDilution: item.post_dilution,
            partnerId: item.partner_id,
            partnerGroupId: item.partner_group_id,
            partner: Partner.mapProperties(item.partner),
            // episodes: item.episodes.map((episode: any) => Episode.mapProperties(episode)),
            postDilutionParentId: item.post_dilution_parent,
            postDilutionChildren: item.post_dilution_children && item.post_dilution_children.length > 0 ? item.post_dilution_children.map((child: any) => this.mapProperties(child)) : [],

            // isToggled: false
            // selectedEpisodesCount: 0,
            expandDetails: item.post_dilution_children ? true : false //only expand details if there is anything to show
        }

    }
}

export class Show {
    id: number | null = null;
    name: string = '';
    isShared: boolean = false;
    seasons: Season[] = [];
    partnerGroup: PartnerGroup | null = null;

    static mapProperties(item: any): Show {
        if(item){
            return {
                id: item.id,
                name: item.name?.replace(/ {2,}/g, ' '),
                isShared: item.is_shared, //??
                seasons: (item.seasons || []).map((season: any) => Season.mapProperties(season)),
                partnerGroup: PartnerGroup.mapProperties(item.partner_group)
    
            };
        }
        else{
            return null;
        }

    }
}
